import { useState, useMemo } from "react"
import { Dialog, DialogContent, DialogTrigger } from "../../../../components/ui/dialog"
import { useWidget } from "../../../../providers/widget"
import { useExplorer } from "../../providers"
import { useExplorerCss } from "../../hooks/useExplorerCss"
import { useFetchExplorerMeta, curateOptions, priceFieldForMode } from "../../data"
import { ExploreWidgetConfig, VisitorFilter } from "../../config"

type WhereOption = { id: number | string; name: string; description?: string }

// Per-source config: which visitor-filter param the Where field writes, which
// filterOptions dimension curates it, and the modal heading (WP: where_source).
const SOURCE_CONFIG = {
  city: { filterKey: 'cities', curationKey: 'cities', label: 'All Cities' },
  location: { filterKey: 'locations', curationKey: 'locations', label: 'Regions' },
  group: { filterKey: 'groups', curationKey: 'groups', label: 'Groups' },
  state: { filterKey: 'state', curationKey: 'states', label: 'States' },
  country: { filterKey: 'country', curationKey: 'countries', label: 'Countries' },
} as const

export default function LocationFilter() {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const cssString = useExplorerCss()
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState("")

  // 'zone' is rendered by ZoneFilter (FilterBar routes it), so LocationFilter only
  // ever handles the single-taxonomy sources; fall back to city defensively.
  const rawSource = options.whereFieldSource ?? 'city'
  const source = rawSource === 'zone' ? 'city' : rawSource
  const { filterKey, curationKey, label } = SOURCE_CONFIG[source]

  const [selected, setSelected] = useState<string[]>(
    state.visitorFilters[filterKey]?.split(',').filter(Boolean) ?? []
  )

  // priceField isn't used here, but passing it keeps the query key identical to
  // AdditionalFilters and the SSR prefetch — one meta entry, no duplicate fetch.
  const { data: metaData } = useFetchExplorerMeta(feedId, options.filter?.platform, priceFieldForMode(options.priceDisplay.noDates))
  const meta = metaData?.result

  // Option universe for the selected source. City prefers the rich cities list
  // (with descriptions); location/group come from the id/name taxonomy lists.
  const sourceOptions = useMemo<WhereOption[]>(() => {
    let list: WhereOption[]
    if (!meta) list = []
    else if (source === 'location') list = meta.taxonomies?.locations ?? []
    else if (source === 'group') list = meta.taxonomies?.groups ?? []
    else if (source === 'state') list = meta.location?.states ?? []
    else if (source === 'country') list = meta.location?.countries ?? []
    else if (meta.location?.cities?.length) list = meta.location.cities
    else list = meta.taxonomies?.cities ?? []
    // Curate to the admin's chosen values + order (#511); empty = show all.
    return curateOptions(list, options.filterOptions?.[curationKey]?.split(',').filter(Boolean))
  }, [meta, source, curationKey, options.filterOptions])

  const filteredOptions = useMemo(() => {
    if (!keyword) return sourceOptions
    return sourceOptions.filter(o => o.name.toLowerCase().includes(keyword.toLowerCase()))
  }, [sourceOptions, keyword])

  const displayValue = useMemo(() => {
    if (selected.length === 0) return ''
    if (sourceOptions.length === 0) return selected.join(', ')
    return sourceOptions
      .filter(o => selected.includes(String(o.id)))
      .map(o => o.name)
      .join(', ')
  }, [selected, sourceOptions])

  const handleToggle = (id: string) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const handleApply = () => {
    setFilter({ [filterKey]: selected.join(',') } as Partial<VisitorFilter>)
    setOpen(false)
  }

  const handleClear = () => {
    setSelected([])
    setFilter({ [filterKey]: undefined } as Partial<VisitorFilter>)
  }

  return (
    <div className="hfse-form-zone-field-container">
      <div className="field-control hfse-form-zone-field-control">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <div className="hfse-form-field-trigger">
              <span className="hfse-form-field-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <input
                type="text"
                className="hk-input hfse-form-zone-options-trigger"
                placeholder="Anywhere"
                value={displayValue}
                readOnly
              />
            </div>
          </DialogTrigger>
          <DialogContent className="hfse-form-modal hfse-form-zone-options" elevateDom cssString={cssString} title="Where" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="hk-modal-header">
              <span className="hk-h4" style={{ paddingBottom: '10px' }}>
                Where
              </span>
            </div>

            <div className="hk-modal-body hk-flex-1">
              <div className="hk-position-relative hk-margin-bottom hfse-form-zone-input">
                <span className="hfse-form-zone-input-fields">
                  <span className="hfse-form-zone-selections">
                    {selected.map(id => {
                      const opt = sourceOptions.find(o => String(o.id) === id)
                      return opt ? (
                        <span
                          key={id}
                          className="hfse-form-zone-selection"
                          onClick={() => handleToggle(id)}
                        >
                          {opt.name} ×
                        </span>
                      ) : null
                    })}
                  </span>
                  <span className="hfse-form-zone-keyword-container">
                    <input
                      type="text"
                      className="hfse-form-zone-keyword"
                      placeholder="Anywhere"
                      value={keyword}
                      onChange={e => setKeyword(e.target.value)}
                    />
                  </span>
                </span>
              </div>

              <div className="hfse-form-zone-label hfse-form-zone-label-cities">{label}</div>
              <div className="hfse-form-zone-subfield hfse-form-zone-subfield-cities">
                {filteredOptions.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`hfse-form-zone-btn hfse-form-zone-city ${selected.includes(String(opt.id)) ? 'hfse-selected' : ''}`}
                    data-value={opt.id}
                    onClick={() => handleToggle(String(opt.id))}
                  >
                    <div className="name">{opt.name}</div>
                    {opt.description && <div className="subheading">{opt.description}</div>}
                  </button>
                ))}
                {filteredOptions.length === 0 && (
                  <div className="hfse-form-zone-no-results">No items found.</div>
                )}
              </div>
            </div>

            <div className="hk-modal-footer hk-flex">
              {selected.length > 0 && (
                <button type="button" className="hk-button hk-button-link hfse-modal-reset-button" onClick={handleClear}>
                  Clear Filters
                </button>
              )}
              <button
                type="button"
                className="hk-button hk-button-small bg-accent text-accent-foreground hk-margin-auto-left hfse-modal-next-button"
                onClick={handleApply}
                style={{ marginLeft: 'auto' }}
              >
                Apply
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
